import type { ReactNode } from 'react'
import { cn } from './cn'

/**
 * Pastille d'état.
 *
 * Reprend les variantes du design system (`lav`, `ink`, `outline`, `tag`) et
 * ajoute les trois états que l'app manipule en permanence et que le DS, pensé
 * pour le marketing, ne couvrait pas : `ok`, `neutral`, `alert`.
 *
 * `alert` puise dans les couleurs système corail — hors charte, réservées aux
 * retards et aux alertes. Ne pas l'employer pour attirer l'œil sur autre chose :
 * si tout est en alerte, plus rien ne l'est.
 */

type Variant =
  | 'lav'
  | 'ink'
  | 'outline'
  | 'tag'
  | 'ok'
  | 'neutral'
  | 'alert'
  // Oasis 2027 — étiquettes « stickers » : fait (lavande), à faire (rose), à venir ou neutre (trait).
  | 'lavande'
  | 'rose'
  | 'trait'

const VARIANTS: Record<Variant, string> = {
  lav: 'bg-lavender text-ink',
  ink: 'bg-ink text-cream',
  outline: 'bg-transparent text-ink border border-hairline-strong',
  tag: 'bg-lavender/[0.18] text-lavender-deep',
  ok: 'bg-lavender-soft text-ink',
  neutral: 'bg-cream-warm text-ink-soft',
  alert: 'bg-alert-bg text-alert-fg',
  /* Oasis 2027, sous `[data-da="2027"]` seulement : découpe crème et légère
     inclinaison, comme les étiquettes de formulaire du site. */
  lavande:
    'bg-lavender text-ink -rotate-2 text-[13px] font-extrabold tracking-[-0.01em] shadow-[0_0_0_3px_var(--creme),0_3px_6px_rgba(43,23,72,0.18)]',
  rose: 'bg-rose text-ink -rotate-2 text-[13px] font-extrabold tracking-[-0.01em] shadow-[0_0_0_3px_var(--creme),0_3px_6px_rgba(43,23,72,0.18)]',
  trait:
    'bg-surface text-ink -rotate-2 text-[13px] font-extrabold tracking-[-0.01em] shadow-[inset_0_0_0_1.5px_var(--ink),0_0_0_3px_var(--creme),0_3px_6px_rgba(43,23,72,0.14)]',
}

/* Les étiquettes 2027 portent leur propre corps (13px, 800) : la typographie
   historique, en 11,5px et en capitales espacées, ne s'y ajoute pas. */
const ETIQUETTES_2027: ReadonlySet<Variant> = new Set(['lavande', 'rose', 'trait'])

export function Badge({
  variant = 'neutral',
  dot = false,
  children,
  className,
}: {
  variant?: Variant
  /** Point de statut devant le libellé, comme dans le design system. */
  dot?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 whitespace-nowrap',
        !ETIQUETTES_2027.has(variant) && 'text-[11.5px] font-bold tracking-[0.02em]',
        VARIANTS[variant],
        className,
      )}
    >
      {dot && <span className="size-1.5 rounded-pill bg-current" aria-hidden="true" />}
      {children}
    </span>
  )
}
